/**
 * Status surface — what the panel shows when there is no live browser to show.
 *
 * Every state names what happened and, where an action exists, offers it. A
 * blank pane is the one outcome this must never produce: the user cannot tell a
 * connecting session from a failed one, and both look like a broken app.
 *
 * @module BrowserStatusSurface
 */
import {
  ActivityIcon,
  AlertCircleIcon,
  CameraIcon,
  ExternalLinkIcon,
  GlobeIcon,
  Loader2Icon,
} from "lucide-react";

import { cn } from "~/lib/utils";

import { resolveBrowserPreviewStateChromeVm } from "../../components/BrowserPreviewPanel.logic";

export function BrowserPreviewState(props: {
  readonly status: string;
  readonly title: string;
  readonly description: string;
  readonly canRefresh: boolean;
  readonly canOpenExternal: boolean;
  readonly onRefresh?: () => void;
  readonly onOpenExternal?: () => void;
}) {
  const chromeVm = resolveBrowserPreviewStateChromeVm({
    status: props.status,
    canRefresh: props.canRefresh,
    canOpenExternal: props.canOpenExternal,
    hasRefreshHandler: Boolean(props.onRefresh),
    hasOpenExternalHandler: Boolean(props.onOpenExternal),
  });
  const Icon =
    chromeVm.icon === "alert"
      ? AlertCircleIcon
      : chromeVm.icon === "loader"
        ? Loader2Icon
        : chromeVm.icon === "activity"
          ? ActivityIcon
          : GlobeIcon;

  return (
    <div className="flex h-full items-center justify-center px-5 text-center">
      <div className="flex max-w-sm flex-col items-center gap-3">
        <div
          className={cn(
            "flex size-11 items-center justify-center rounded-xl border bg-background/80",
            chromeVm.isProblem
              ? "border-destructive-border text-destructive"
              : "border-border text-primary",
          )}
        >
          <Icon className={cn("size-5", chromeVm.isConnecting && "animate-spin")} />
        </div>
        <div>
          <h3 className="text-sm font-medium text-foreground">{props.title}</h3>
          <p className="mt-1 text-sm leading-5 text-muted-foreground">{props.description}</p>
        </div>
        {chromeVm.showActions ? (
          <div className="flex flex-wrap items-center justify-center gap-2">
            {chromeVm.showRefresh ? (
              <button
                type="button"
                className="inline-flex h-8 items-center gap-1 rounded-md border border-border px-2 text-xs font-medium text-foreground hover:bg-accent"
                onClick={props.onRefresh}
              >
                <CameraIcon className="size-3.5" />
                Refresh capture
              </button>
            ) : null}
            {chromeVm.showOpenExternal ? (
              <button
                type="button"
                className="inline-flex h-8 items-center gap-1 rounded-md border border-border px-2 text-xs text-muted-foreground hover:bg-accent hover:text-foreground"
                onClick={props.onOpenExternal}
              >
                <ExternalLinkIcon className="size-3.5" />
                Open externally
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
